import { defineStore } from 'pinia'
import { ref } from 'vue'
import Network from '@/Network'
import { getApiErrorMessage } from '@/utils/apiError'
import type { SpecialtyOptionsResponse } from '@/Network/Types/Responses/SpecialtyResponses'

export const SPECIALTIES_LOAD_ERROR = 'Não foi possível carregar as especialidades'

export const useSpecialtyStore = defineStore('specialty', () => {
  const byCompany = ref<Record<number, SpecialtyOptionsResponse>>({})
  const loadErrorByCompany = ref<Record<number, string | null>>({})
  const inFlight = new Map<number, Promise<SpecialtyOptionsResponse | null>>()

  const optionsOf = (companyId: number | null | undefined): SpecialtyOptionsResponse | null =>
    companyId ? (byCompany.value[companyId] ?? null) : null

  /** `key` no formato da grade: `o:<id>` (oficial) ou `c:<id>` (da empresa). */
  function specialtyName(key: string): string | null {
    const [kind, rawId] = key.split(':')
    const id = Number(rawId)
    for (const options of Object.values(byCompany.value)) {
      const list = kind === 'o' ? options.official : options.company
      const found = list.find((option) => option.id === id)
      if (found) return found.name
    }
    return null
  }

  async function fetchOptions(
    companyId: number,
    options: { force?: boolean } = {}
  ): Promise<SpecialtyOptionsResponse | null> {
    if (!options.force && byCompany.value[companyId]) return byCompany.value[companyId]
    const pending = inFlight.get(companyId)
    if (pending && !options.force) return pending
    // Mesma regra do useSectorStore: resposta de request substituída é descartada.
    const isCurrent = () => inFlight.get(companyId) === request
    const request: Promise<SpecialtyOptionsResponse | null> = (async () => {
      try {
        const result = await Network.specialty.getOptions(companyId)
        if (!isCurrent()) return optionsOf(companyId)
        byCompany.value = { ...byCompany.value, [companyId]: result }
        loadErrorByCompany.value = { ...loadErrorByCompany.value, [companyId]: null }
        return result
      } catch (error) {
        if (!isCurrent()) return optionsOf(companyId)
        // fallback intencional: o combobox mostra loadErrorByCompany e segue sem opções.
        loadErrorByCompany.value = {
          ...loadErrorByCompany.value,
          [companyId]: getApiErrorMessage(error, SPECIALTIES_LOAD_ERROR)
        }
        return null
      } finally {
        if (isCurrent()) inFlight.delete(companyId)
      }
    })()
    inFlight.set(companyId, request)
    return request
  }

  function $reset(): void {
    byCompany.value = {}
    loadErrorByCompany.value = {}
    inFlight.clear()
  }

  return { byCompany, loadErrorByCompany, optionsOf, specialtyName, fetchOptions, $reset }
})
